import styled from 'styled-components';

// Attachment Upload Components
export const AttachmentSection = styled.div`
  position: relative;
  background: ${(props) =>
    props.isDragActive
      ? props.theme.name === 'dark'
        ? 'rgba(250,70,22,0.08)'
        : 'rgba(250,70,22,0.04)'
      : props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.03)'
      : '#FAFBFC'};
  border: 1px dashed
    ${(props) =>
      props.isDragActive
        ? props.theme?.colors?.primary || '#FA4616'
        : props.hasFiles
        ? props.theme.name === 'dark'
          ? 'rgba(250,70,22,0.3)'
          : 'rgba(250,70,22,0.4)'
        : props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : '#D1D5DB'};
  border-width: ${(props) => (props.isDragActive ? '2px' : '1px')};
  border-radius: 8px;
  margin-top: 12px;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  overflow: hidden;
`;

export const DragOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250,70,22,0.12)'
      : 'rgba(250,70,22,0.06)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 8px;
  pointer-events: none;
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  font-size: 14px;
  font-weight: 500;
  gap: 8px;
`;

export const AttachmentHeader = styled.div`
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
`;

export const AddImageButton = styled.button`
  padding: 6px 14px;
  background: ${(props) =>
    props.disabled ? '#9CA3AF' : props.theme?.colors?.primary || '#FA4616'};
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover:not(:disabled) {
    background: #e03e0c;
    transform: translateY(-1px);
  }
`;

export const AttachmentInfo = styled.span`
  font-size: 13px;
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  flex: 1;
`;

export const AttachmentStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export const FileCountBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250,70,22,0.15)'
      : 'rgba(250,70,22,0.1)'};
  color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
`;

export const ExpandIcon = styled.div`
  color: ${(props) => (props.theme.name === 'dark' ? '#9CA3AF' : '#6B7280')};
  transition: transform 0.2s ease;
  transform: ${(props) => (props.expanded ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const AttachmentContent = styled.div`
  padding: 0 0 12px 0;
  animation: slideDown 0.2s ease;
  overflow: hidden;
  width: 100%;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const FileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, 100px);
  gap: 10px;
  padding: 0 12px;
`;

export const FileItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
  cursor: pointer;
  border: 2px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.1)'};
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)'};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain; /* Changed from cover to contain to show full image */
  }

  /* Only apply hover styles on devices that support true hover (mouse) */
  @media (hover: hover) and (pointer: fine) {
    &:hover .file-overlay {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

export const FileOverlay = styled.div.attrs({ className: 'file-overlay' })`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  opacity: ${(props) => (props.isActive ? 1 : 0)};
  pointer-events: ${(props) => (props.isActive ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

export const OverlayButton = styled.button`
  width: 100%;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  gap: 6px;
  transition: all 0.15s ease;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }

  &:active {
    transform: scale(0.97);
  }
`;

export const ViewFileButton = styled(OverlayButton)`
  background: #3b82f6;

  &:hover {
    background: #2563eb;
  }
`;

export const RemoveFileButton = styled(OverlayButton)`
  background: #ef4444;

  &:hover {
    background: #dc2626;
  }
`;

export const AddMoreArea = styled.div`
  aspect-ratio: 1;
  box-sizing: border-box;
  border: 2px dashed
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.2)'
        : 'rgba(0,0,0,0.2)'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.02)'
      : 'rgba(0,0,0,0.02)'};
  color: ${(props) => (props.theme.name === 'dark' ? '#6B7280' : '#9CA3AF')};

  &:hover {
    border-color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
    background: rgba(250, 70, 22, 0.05);
    color: ${(props) => props.theme?.colors?.primary || '#fa4616'};
  }
`;

// Image Preview Modal Components
export const ImagePreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export const ImagePreviewContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const ImagePreviewImage = styled.img`
  max-width: 90vw;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 4px;
`;

export const ImagePreviewClose = styled.button`
  position: absolute;
  top: -40px;
  right: -10px;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

export const ImagePreviewFileName = styled.div`
  margin-top: 12px;
  color: #ffffff;
  font-size: 14px;
  max-width: 90vw;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ImagePreviewPDF = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
`;

export const ImagePreviewPDFLink = styled.a`
  margin-top: 16px;
  padding: 10px 20px;
  background: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  color: #ffffff;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #e03e0c;
  }
`;
